import mixpanelService from "./mixpanelService";
import mixpanelEvents, { getScreenName } from "../../config/mixpanelEvents";

// Raises form_clicked on the first interaction with a form control.
// Only the field's identity, type and whether it now holds a value are sent -
// never what the user typed or picked.

export interface IFormFieldMeta {
  element?: HTMLInputElement | null; // read the label from, never the value
  fieldName?: string;
  fieldLabel?: string; // when the caller already knows it
  fieldType?: string;
  hasValue?: boolean;
}

const MAX_LABEL_LENGTH = 60;

function cleanLabel(text: string): string {
  return text
    .replace(/\s+/g, " ")
    .replace(/[*:]+\s*$/, "")
    .trim()
    .slice(0, MAX_LABEL_LENGTH);
}

// "firstName" -> "First Name"
function humanize(name: string): string {
  return cleanLabel(
    name
      .replace(/[_-]+/g, " ")
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

// Same order a screen reader resolves an accessible name in.
function resolveFieldLabel(el?: HTMLInputElement | null): string {
  if (!el) return "";
  try {
    const fromLabel = el.labels && el.labels[0]?.textContent;
    if (fromLabel && cleanLabel(fromLabel)) return cleanLabel(fromLabel);

    const labelledBy = el.getAttribute("aria-labelledby");
    if (labelledBy) {
      const fromLabelledBy = labelledBy
        .split(/\s+/)
        .map((id) => document.getElementById(id)?.textContent || "")
        .join(" ");
      if (cleanLabel(fromLabelledBy)) return cleanLabel(fromLabelledBy);
    }

    const ariaLabel = el.getAttribute("aria-label");
    if (ariaLabel && cleanLabel(ariaLabel)) return cleanLabel(ariaLabel);

    // some screens render a <label> next to the field with no htmlFor
    let node: HTMLElement | null = el.parentElement;
    for (let depth = 0; node && depth < 4; depth++) {
      const sibling = node.querySelector(":scope > label");
      if (sibling && cleanLabel(sibling.textContent || "")) {
        return cleanLabel(sibling.textContent || "");
      }
      node = node.parentElement;
    }

    if (el.placeholder && cleanLabel(el.placeholder)) {
      return cleanLabel(el.placeholder);
    }
  } catch (e) {
    console.error("resolveFieldLabel failed", e);
  }
  return "";
}

let trackedScreen = "";
let lastTrackedField = "";

export function trackFormFieldEntered(meta: IFormFieldMeta) {
  try {
    const screen_name = getScreenName(window.location.pathname);
    if (!screen_name) return;

    const field_name = meta.fieldName || cleanLabel(meta.fieldLabel || "");
    if (!field_name) return;

    const text =
      cleanLabel(meta.fieldLabel || "") ||
      resolveFieldLabel(meta.element) ||
      humanize(field_name);

    if (screen_name !== trackedScreen) {
      trackedScreen = screen_name;
      lastTrackedField = "";
    }
    if (field_name === lastTrackedField) return;
    lastTrackedField = field_name;

    mixpanelService({
      event: mixpanelEvents.FORM_CLICKED,
      screen_name,
      text,
      state: meta.hasValue === false ? "empty" : "filled",
      field_name,
      field_type: meta.fieldType || "text",
      interaction_type: "input_entered",
    });
  } catch (e) {
    console.error("mixpanel form_clicked failed", e);
  }
}
